package app.smartcoding.realrate.presentation

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import app.smartcoding.realrate.domain.model.CreditInputs
import app.smartcoding.realrate.domain.model.CreditInputsText
import app.smartcoding.realrate.domain.model.CreditResult
import app.smartcoding.realrate.domain.model.CreditUiState
import app.smartcoding.realrate.domain.model.ErrorType
import app.smartcoding.realrate.domain.model.Payment
import app.smartcoding.realrate.domain.model.creditCardField.Loan
import app.smartcoding.realrate.domain.model.creditCardField.LoanField
import app.smartcoding.realrate.domain.model.creditCardField.MonthsField
import app.smartcoding.realrate.domain.model.creditCardField.PaymentField
import app.smartcoding.realrate.domain.tools.toDoubleOrZero
import app.smartcoding.realrate.domain.usecase.AnalyzeCreditUseCase
import app.smartcoding.realrate.domain.usecase.LoanCalculator
import app.smartcoding.realrate.domain.usecase.PaymentCalculator
import app.smartcoding.realrate.presentation.util.filterToDoubleString
import app.smartcoding.realrate.presentation.util.toCleanString
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChangedBy
import kotlinx.coroutines.flow.filterIsInstance
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

const val MAX_NUMBER_DECIMAL = 100_000_000.0

@OptIn(FlowPreview::class)
@Stable
class CreditViewModel : ViewModel() {
    private val analyzeCreditUseCase: AnalyzeCreditUseCase = AnalyzeCreditUseCase()

    private var _uiState by mutableStateOf<CreditUiState>(CreditUiState.Loading(CreditInputs()))

    var result: CreditUiState.Success? by mutableStateOf(null)
        private set
    var error: CreditUiState.Error? by mutableStateOf(null)
        private set
    var inputs: CreditInputs? by mutableStateOf(null)
        private set

    private fun updateUiState(block: (CreditUiState) -> CreditUiState) {
        val uiState = block(_uiState)
        when (uiState) {
            is CreditUiState.Loading -> {
                inputs = uiState.inputs
            }

            is CreditUiState.Success -> {
                inputs = uiState.inputs
                result = uiState
                error = null
            }

            is CreditUiState.Error -> {
                inputs = uiState.inputs
                error = uiState
                result = null
            }
        }
        _uiState = uiState
    }

    fun onToggleShowDetailedLoanAmountInput() {
        updateUiState { state ->
            CreditUiState.Loading(state.inputs.copy(showDetailedLoanAmountInput = !state.inputs.showDetailedLoanAmountInput))
        }
    }

    fun onToggleLockedTaxRate() {
        updateUiState { state ->
            CreditUiState.Loading(state.inputs.copy(isLockedTaxRate = !state.inputs.isLockedTaxRate))
        }
    }

    fun onToggleShowDetailedMountsInput() {
        updateUiState { state ->
            CreditUiState.Loading(state.inputs.copy(showDetailedMountsInput = !state.inputs.showDetailedMountsInput))
        }
    }

    fun onToggleShowDetailedMountPaymentInput() {
        updateUiState { state ->
            CreditUiState.Loading(state.inputs.copy(showDetailedPaymentInput = !state.inputs.showDetailedPaymentInput))
        }
    }


    fun onLoanFieldsChanged(field: LoanField) {
        updateUiState { state ->
            val currentInputs = state.inputs

            when (field) {
                is LoanField.RequestedAmount -> {
                    val newLoanRequestedText = field.textFieldValue.text.filterToDoubleString(
                        delta = field.delta,
                        minNumber = 1.0,
                        maxNumber = MAX_NUMBER_DECIMAL,
                        isInt = false
                    )
                    val newLoanRequestedAmount =
                        Loan.RequestedAmount(newLoanRequestedText.toDoubleOrZero())

                    val newTaxFee = LoanCalculator.taxFee(
                        requestedAmount = newLoanRequestedAmount,
                        taxRate = Loan.TaxRate(currentInputs.loanTaxRate.text.toDoubleOrZero())
                    )

                    if (newTaxFee is Loan.Error) return@updateUiState CreditUiState.Error(
                        inputs = currentInputs,
                        error = newTaxFee.error
                    ) else newTaxFee as Loan.TaxFee

                    val newTaxFeeText = newTaxFee.value.toCleanString()

                    val newLoanReceivedAmount = LoanCalculator.receivedAmount(
                        requestedAmount = newLoanRequestedAmount,
                        taxFee = newTaxFee
                    )

                    if (newLoanReceivedAmount is Loan.Error) return@updateUiState CreditUiState.Error(
                        inputs = currentInputs,
                        error = newLoanReceivedAmount.error
                    ) else newLoanReceivedAmount as Loan.ReceivedAmount

                    val newLoanAmountText = newLoanReceivedAmount.value.toCleanString()

                    val newInputs = currentInputs.copy(
                        loanRequestedAmount = TextFieldValue(
                            text = newLoanRequestedText,
                            selection = field.textFieldValue.selection
                        ),
                        loanReceivedAmount = TextFieldValue(
                            text = newLoanAmountText,
                            selection = TextRange(newLoanAmountText.length)
                        ),
                        loanTaxFee = TextFieldValue(
                            text = newTaxFeeText,
                            selection = TextRange(newTaxFeeText.length)
                        )
                    )
                    CreditUiState.Loading(newInputs)
                }

                is LoanField.TaxFee -> {
                    val newTaxFeeText = field.textFieldValue.text.filterToDoubleString(
                        delta = field.delta,
                        minNumber = 0.0,
                        maxNumber = (currentInputs.loanRequestedAmount.text.toDoubleOrZero() - 0.01).coerceAtLeast(
                            0.0
                        ),
                        isInt = false
                    )

                    val newTaxFee = Loan.TaxFee(newTaxFeeText.toDoubleOrZero())

                    val newTaxRate = LoanCalculator.taxRate(
                        requestedAmount = Loan.RequestedAmount(currentInputs.loanRequestedAmount.text.toDoubleOrZero()),
                        taxFee = newTaxFee
                    )

                    if (newTaxRate is Loan.Error) return@updateUiState CreditUiState.Error(
                        inputs = currentInputs,
                        error = newTaxRate.error
                    ) else newTaxRate as Loan.TaxRate

                    val newTaxRateText = newTaxRate.value.toCleanString()

                    val newLoanAmount = LoanCalculator.receivedAmount(
                        requestedAmount = Loan.RequestedAmount(currentInputs.loanRequestedAmount.text.toDoubleOrZero()),
                        taxRate = newTaxRate
                    )

                    if (newLoanAmount is Loan.Error) return@updateUiState CreditUiState.Error(
                        inputs = currentInputs,
                        error = newLoanAmount.error
                    ) else newLoanAmount as Loan.ReceivedAmount

                    val newLoanAmountText = newLoanAmount.value.toCleanString()

                    val newInputs = currentInputs.copy(
                        loanTaxRate = TextFieldValue(
                            newTaxRateText,
                            TextRange(newTaxRateText.length)
                        ),
                        loanTaxFee = TextFieldValue(newTaxFeeText, field.textFieldValue.selection),
                        loanReceivedAmount = TextFieldValue(
                            newLoanAmountText,
                            TextRange(newLoanAmountText.length)
                        )
                    )

                    CreditUiState.Loading(newInputs)
                }

                is LoanField.TaxRate -> {
                    val newTaxRateText = field.textFieldValue.text.filterToDoubleString(
                        delta = field.delta,
                        minNumber = 0.0,
                        maxNumber = 99.99,
                        isInt = false
                    )
                    val newTaxRate = Loan.TaxRate(newTaxRateText.toDoubleOrZero())

                    val requestedAmount =
                        Loan.RequestedAmount(currentInputs.loanRequestedAmount.text.toDoubleOrZero())

                    val newReceivedAmount = LoanCalculator.receivedAmount(
                        requestedAmount = requestedAmount,
                        taxRate = newTaxRate
                    )

                    if (newReceivedAmount is Loan.Error) return@updateUiState CreditUiState.Error(
                        inputs = currentInputs,
                        error = newReceivedAmount.error
                    ) else newReceivedAmount as Loan.ReceivedAmount

                    val newReceivedAmountText = newReceivedAmount.value.toCleanString()

                    val newTaxFee = LoanCalculator.taxFee(
                        requestedAmount = requestedAmount,
                        taxRate = newTaxRate
                    )

                    if (newTaxFee is Loan.Error) return@updateUiState CreditUiState.Error(
                        inputs = currentInputs,
                        error = newTaxFee.error
                    ) else newTaxFee as Loan.TaxFee

                    val newTaxFeeText = newTaxFee.value.toCleanString()

                    val newInputs = currentInputs.copy(
                        loanTaxRate = TextFieldValue(
                            text = newTaxRateText,
                            selection = field.textFieldValue.selection
                        ),
                        loanTaxFee = TextFieldValue(
                            text = newTaxFeeText,
                            selection = TextRange(newTaxFeeText.length)
                        ),
                        loanReceivedAmount = TextFieldValue(
                            text = newReceivedAmountText,
                            selection = TextRange(newReceivedAmountText.length)
                        )
                    )
                    CreditUiState.Loading(newInputs)
                }

                is LoanField.ReceivedAmount -> {
                    val isLockedTaxRate = currentInputs.isLockedTaxRate

                    val newReceivedAmountText = field.textFieldValue.text.filterToDoubleString(
                        delta = field.delta,
                        minNumber = 1.0,
                        maxNumber = MAX_NUMBER_DECIMAL,
                        isInt = false
                    )
                    val newReceivedAmount =
                        Loan.ReceivedAmount(newReceivedAmountText.toDoubleOrZero())

                    val newInputs: CreditInputs = if (isLockedTaxRate) {
                        val taxRate = Loan.TaxRate(currentInputs.loanTaxRate.text.toDoubleOrZero())

                        val newRequestedAmount = LoanCalculator.requestedAmount(
                            receivedAmount = newReceivedAmount,
                            taxRate = taxRate
                        )

                        if (newRequestedAmount is Loan.Error) return@updateUiState CreditUiState.Error(
                            inputs = currentInputs,
                            error = newRequestedAmount.error
                        ) else newRequestedAmount as Loan.RequestedAmount

                        val newRequestedAmountText = newRequestedAmount.value.toCleanString()

                        val newTaxFee = LoanCalculator.taxFee(
                            requestedAmount = newRequestedAmount,
                            taxRate = taxRate
                        )

                        if (newTaxFee is Loan.Error) return@updateUiState CreditUiState.Error(
                            inputs = currentInputs,
                            error = newTaxFee.error
                        ) else newTaxFee as Loan.TaxFee

                        val newTaxFeeText = newTaxFee.value.toCleanString()

                        currentInputs.copy(
                            loanReceivedAmount = TextFieldValue(
                                text = newReceivedAmountText,
                                selection = field.textFieldValue.selection
                            ),
                            loanRequestedAmount = TextFieldValue(
                                text = newRequestedAmountText,
                                selection = TextRange(newRequestedAmountText.length)
                            ),
                            loanTaxFee = TextFieldValue(
                                text = newTaxFeeText,
                                selection = TextRange(newTaxFeeText.length)
                            )
                        )
                    } else {
                        val newTaxRate = LoanCalculator.taxRate(
                            receivedAmount = newReceivedAmount,
                            requestedAmount = Loan.RequestedAmount(currentInputs.loanRequestedAmount.text.toDoubleOrZero()),
                        )

                        if (newTaxRate is Loan.Error) return@updateUiState CreditUiState.Error(
                            inputs = currentInputs,
                            error = newTaxRate.error
                        ) else newTaxRate as Loan.TaxRate

                        val newTaxRateText = newTaxRate.value.toCleanString()

                        val newTaxFee = LoanCalculator.taxFee(
                            requestedAmount = Loan.RequestedAmount(currentInputs.loanRequestedAmount.text.toDoubleOrZero()),
                            taxRate = newTaxRate
                        )

                        if (newTaxFee is Loan.Error) return@updateUiState CreditUiState.Error(
                            inputs = currentInputs,
                            error = newTaxFee.error
                        ) else newTaxFee as Loan.TaxFee

                        val newTaxFeeText = newTaxFee.value.toCleanString()

                        currentInputs.copy(
                            loanReceivedAmount = TextFieldValue(
                                text = newReceivedAmountText,
                                selection = field.textFieldValue.selection
                            ),
                            loanTaxFee = TextFieldValue(
                                text = newTaxFeeText,
                                selection = TextRange(newTaxFeeText.length)
                            ),
                            loanTaxRate = TextFieldValue(
                                text = newTaxRateText,
                                selection = TextRange(newTaxRateText.length)
                            )
                        )
                    }

                    CreditUiState.Loading(newInputs)
                }
            }
        }
    }

    fun onMonthsFieldChanged(field: MonthsField) {
        inputs?.let { inputs ->
            updateUiState { state ->
                when (field) {
                    is MonthsField.MonthsTotal -> {
                        val monthsTotal = field.textFieldValue.text.filterToDoubleString(
                            delta = field.delta,
                            minNumber = 1.0,
                            maxNumber = 600.0,
                            isInt = true
                        )
                        val months = (monthsTotal.toIntOrNull() ?: 0) % 12
                        val years = (monthsTotal.toIntOrNull() ?: 0) / 12
                        val newInputs: CreditInputs = state.inputs.copy(
                            termsMonthsTotal = TextFieldValue(
                                monthsTotal,
                                field.textFieldValue.selection
                            ),
                            termsMonths = TextFieldValue(months.toString()),
                            termsYears = TextFieldValue(years.toString())
                        )
                        CreditUiState.Loading(newInputs)
                    }

                    is MonthsField.Months -> {
                        val months = field.textFieldValue.text.filterToDoubleString(
                            delta = field.delta,
                            minNumber = 0.0,
                            maxNumber = 11.0,
                            isInt = true
                        )
                        val years = inputs.termsYears.text.toIntOrNull() ?: 0
                        val monthsTotal = (months.toIntOrNull() ?: 0) + years * 12
                        val newInputs: CreditInputs = state.inputs.copy(
                            termsMonths = TextFieldValue(months, field.textFieldValue.selection),
                            termsMonthsTotal = TextFieldValue(monthsTotal.toString())
                        )
                        CreditUiState.Loading(newInputs)
                    }

                    is MonthsField.Years -> {
                        val years = field.textFieldValue.text.filterToDoubleString(
                            delta = field.delta,
                            minNumber = 0.0,
                            maxNumber = 50.0,
                            isInt = true
                        )
                        val months = inputs.termsMonths.text.toIntOrNull() ?: 0
                        val totalMonths = (years.toIntOrNull() ?: 0) * 12 + months
                        val newInputs: CreditInputs = state.inputs.copy(
                            termsYears = TextFieldValue(years, field.textFieldValue.selection),
                            termsMonthsTotal = TextFieldValue(
                                totalMonths.toString(),
                                field.textFieldValue.selection
                            )
                        )
                        CreditUiState.Loading(newInputs)
                    }
                }
            }
        }
    }

    fun onPaymentFieldsChanged(field: PaymentField) {
        updateUiState { state ->
            val currentInputs = state.inputs

            when (field) {
                is PaymentField.PaymentTotal -> {
                    val text = field.textFieldValue.text.filterToDoubleString(
                        delta = field.delta,
                        minNumber = 0.0,
                        maxNumber = MAX_NUMBER_DECIMAL / 10,
                        isInt = false
                    )
                    val newPaymentTotal = Payment.PaymentTotal(text.toDoubleOrZero())

                    val insurance =
                        Payment.Insurance(currentInputs.paymentInsurance.text.toDoubleOrZero())
                    val fees = Payment.Fees(currentInputs.paymentFees.text.toDoubleOrZero())

                    val newMainPayment = PaymentCalculator.calculateMainPayment(
                        paymentTotal = newPaymentTotal,
                        insurance = insurance,
                        fees = fees
                    )

                    if (newMainPayment is Payment.Error) return@updateUiState CreditUiState.Error(
                        inputs = currentInputs,
                        error = newMainPayment.error
                    ) else newMainPayment as Payment.MainPayment

                    val newMainPaymentText = newMainPayment.value.toCleanString()

                    val newInputs: CreditInputs = currentInputs.copy(
                        paymentTotal = TextFieldValue(text, field.textFieldValue.selection),
                        paymentMain = TextFieldValue(
                            newMainPaymentText,
                            TextRange(newMainPaymentText.length)
                        )
                    )
                    CreditUiState.Loading(newInputs)
                }

                is PaymentField.MainPayment, is PaymentField.Insurance, is PaymentField.Fees -> {
                    val updatedField = when (field) {
                        is PaymentField.MainPayment -> {
                            val text = field.textFieldValue.text.filterToDoubleString(
                                delta = field.delta,
                                minNumber = 0.0,
                                maxNumber = MAX_NUMBER_DECIMAL / 10,
                                isInt = false
                            )
                            currentInputs.copy(
                                paymentMain = TextFieldValue(
                                    text,
                                    field.textFieldValue.selection
                                )
                            )
                        }

                        is PaymentField.Insurance -> {
                            val text = field.textFieldValue.text.filterToDoubleString(
                                delta = field.delta,
                                minNumber = 0.0,
                                maxNumber = MAX_NUMBER_DECIMAL / 10,
                                isInt = false
                            )
                            currentInputs.copy(
                                paymentInsurance = TextFieldValue(
                                    text,
                                    field.textFieldValue.selection
                                )
                            )
                        }

                        is PaymentField.Fees -> {
                            val text = field.textFieldValue.text.filterToDoubleString(
                                delta = field.delta,
                                minNumber = 0.0,
                                maxNumber = MAX_NUMBER_DECIMAL / 10,
                                isInt = false
                            )
                            currentInputs.copy(
                                paymentFees = TextFieldValue(
                                    text,
                                    field.textFieldValue.selection
                                )
                            )
                        }

                        is PaymentField.PaymentTotal -> currentInputs
                    }

                    val mainPayment =
                        Payment.MainPayment(updatedField.paymentMain.text.toDoubleOrZero())
                    val insurance =
                        Payment.Insurance(updatedField.paymentInsurance.text.toDoubleOrZero())
                    val fees = Payment.Fees(updatedField.paymentFees.text.toDoubleOrZero())

                    val newPaymentTotal = PaymentCalculator.calculateTotalPayment(
                        mainPayment = mainPayment,
                        insurance = insurance,
                        fees = fees
                    )

                    if (newPaymentTotal is Payment.Error) return@updateUiState CreditUiState.Error(
                        inputs = currentInputs,
                        error = newPaymentTotal.error
                    ) else newPaymentTotal as Payment.PaymentTotal

                    val newPaymentTotalText = newPaymentTotal.value.toCleanString()

                    val newInputs: CreditInputs = updatedField.copy(
                        paymentTotal = TextFieldValue(
                            newPaymentTotalText,
                            TextRange(newPaymentTotalText.length)
                        )
                    )
                    CreditUiState.Loading(newInputs)
                }
            }
        }
    }

    init {
        viewModelScope.launch {
            snapshotFlow { _uiState }
                .filterIsInstance<CreditUiState.Loading>()
                .map { it.inputs }
                .flowOn(Dispatchers.Default)
                .distinctUntilChangedBy { CreditInputsText.fromCreditInput(it) }
                .collectLatest { inputs ->
                    val uiState = withContext(Dispatchers.Default) { calculate(inputs) }
                    updateUiState { uiState }
                }
        }
    }

    suspend fun calculate(inputs: CreditInputs): CreditUiState {
        val amount = inputs.loanReceivedAmount.text.toDoubleOrNull() ?: 0.0
        val months = inputs.termsMonthsTotal.text.toIntOrNull() ?: 0
        val monthlyPaymentValue = inputs.paymentTotal.text.toDoubleOrNull() ?: 0.0
        val startDate = inputs.startDate

        if (amount > 0 && months > 0 && monthlyPaymentValue > 0) {
            if (monthlyPaymentValue * months < amount) {
                return CreditUiState.Error(inputs = inputs, error = ErrorType.PaymentLessThanLoan)
            }

            try {
                val monthlyRate = analyzeCreditUseCase
                    .calculateMonthlyRate(months, monthlyPaymentValue, amount)
                    ?: error("of nominal rate")

                currentCoroutineContext().ensureActive()

                val nominalRate = analyzeCreditUseCase.calculateNominalRate(monthlyRate)
                val apr = analyzeCreditUseCase.calculateEffectiveApr(monthlyRate)

                if (!nominalRate.isFinite() || nominalRate > 1000.0) {
                    return CreditUiState.Error(inputs = inputs, error = ErrorType.RateTooHigh)
                }
                if (!apr.isFinite()) {
                    return CreditUiState.Error(
                        inputs = inputs,
                        error = ErrorType.CalculationOverflow
                    )
                }

                val schedule = analyzeCreditUseCase.generateSchedule(
                    principal = amount,
                    monthlyRate = monthlyRate,
                    months = months,
                    monthlyPayment = monthlyPaymentValue,
                    startDate = startDate
                ).toImmutableList()

                if (schedule.any { it.interest < 0 || it.principal < 0 || it.balance < 0 }) {
                    return CreditUiState.Error(
                        inputs = inputs,
                        error = ErrorType.CalculationOverflow
                    )
                }

                val totalPaid = monthlyPaymentValue * months
                val appreciation = if (amount > 0) (totalPaid - amount) / amount * 100 else 0.0

                return CreditUiState.Success(
                    inputs = inputs,
                    result = CreditResult(
                        nominalRate = nominalRate,
                        effectiveApr = apr,
                        appreciation = appreciation,
                        schedule = schedule
                    )
                )
            } catch (e: Exception) {
                if (e is kotlinx.coroutines.CancellationException) throw e

                return CreditUiState.Error(
                    inputs = inputs,
                    error = ErrorType.CalculationError
                )
            }
        } else {
            return CreditUiState.Error(inputs = inputs, error = ErrorType.CalculationError)
        }
    }
}